<template>
    <Head :title="t('settings.page.title')" />
    <BaseLayout gap="lg" fill-height>
        <BasePageHeader :title="t('settings.page.title')" />
        <AccountSettingsSection
            :user-profile="user_profile"
            :language-choices="language_choices"
            :cookie-consent-choices="cookie_consent_choices"
            :errors="errors[SETTINGS_ERROR_BAG]"
            :endpoints="endpoints"
            :can-receive-emails="userDetails?.can_receive_emails"
            :custom-instructions-enabled="custom_instructions_enabled"
            :mic-consent="mic_consent" />
        <InterfaceLanguageSection
            v-if="ui_language_choices.length > 1"
            :ui-language="uiLanguage"
            :ui-language-choices="ui_language_choices"
            :error="errors[SETTINGS_ERROR_BAG]?.ui_language"
            :profile-endpoint="endpoints.profile"
            :current-page-endpoint="endpoints.current_page" />
        <IntegrationsSection
            v-if="integrations_enabled || mobileAppDownloadUrl"
            :integrations-enabled="integrations_enabled"
            :calendar-integration="calendar_integration"
            :open-calendar-on-mount="openCalendarOnMount"
            :granola-enabled="granola_enabled"
            :granola-connected="granola_connected"
            :granola-connect-url="granola_connect_url"
            :granola-disconnect-url="granola_disconnect_url"
            :workiq-integration="workiq_integration"
            :slack-app-url="slack_app_url"
            :slack-connected="slack_connected"
            :slack-workspaces="slack_workspaces"
            :mobile-app-download-url="mobileAppDownloadUrl" />
        <DeleteAccountSection v-if="!userDetails?.is_demo" class="mt-auto" :delete-account-url="endpoints.delete_account" />
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import type { OrganizationDetails, UserDetails } from "~vue/appTypes";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import type { SlackWorkspaceLink } from "~vue/components/SlackIntegration/SlackIntegrationSettings.vue";
import AccountSettingsSection, { ERROR_BAG as SETTINGS_ERROR_BAG } from "~vue/UserProfile/AccountSettingsSection.vue";
import DeleteAccountSection from "~vue/UserProfile/DeleteAccountSection.vue";
import IntegrationsSection from "~vue/UserProfile/IntegrationsSection.vue";
import InterfaceLanguageSection from "~vue/UserProfile/InterfaceLanguageSection.vue";
import type { Ref } from "vue";
import { computed, inject, onMounted, provide } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(
    defineProps<{
        user_profile: Record<string, unknown>;
        language_choices?: [string, string][];
        ui_language_choices?: [string, string, string][];
        cookie_consent_choices?: [string, string][];
        errors?: Record<string, Record<string, string>>;
        endpoints: Record<string, string>;
        calendar_integration?: Partial<{
            enabled: boolean;
            timezones: string[];
            calendar_provider: string | null;
            dismissed: boolean;
            private_toggle_enabled: boolean;
            allow_private_events: boolean | null;
        }>;
        custom_instructions_enabled?: boolean;
        mic_consent?: Partial<{ policy_enabled: boolean; granted: boolean }>;
        granola_enabled?: boolean;
        granola_connected?: boolean;
        granola_connect_url?: string;
        granola_disconnect_url?: string;
        workiq_integration?: Partial<{
            enabled: boolean;
            connected: boolean;
            connect_url: string;
            status_url: string;
            disconnect_url: string;
        }>;
        integrations_enabled?: boolean;
        slack_app_url?: string;
        slack_connected?: boolean;
        slack_workspaces?: SlackWorkspaceLink[];
    }>(),
    {
        language_choices: () => [],
        ui_language_choices: () => [],
        cookie_consent_choices: () => [],
        errors: () => ({}) as Record<string, Record<string, string>>,
        calendar_integration: () => ({}),
        custom_instructions_enabled: false,
        mic_consent: () => ({}),
        granola_enabled: false,
        granola_connected: false,
        granola_connect_url: "",
        granola_disconnect_url: "",
        workiq_integration: () => ({}),
        integrations_enabled: false,
        slack_app_url: "",
        slack_connected: false,
        slack_workspaces: () => [],
    },
);

provide("calendarIntegration", props.calendar_integration);

const openCalendarOnMount = props.integrations_enabled && !!props.calendar_integration?.enabled && new URLSearchParams(globalThis.location.search).get("open_calendar") === "true";

onMounted(() => {
    if (!openCalendarOnMount) {
        return;
    }
    const url = new URL(globalThis.location.href);
    url.searchParams.delete("open_calendar");
    // Native history writes race Inertia's initial visit, which restores the original URL.
    router.replace({ url: `${url.pathname}${url.search}${url.hash}`, preserveState: true, preserveScroll: true });
});

const userDetails = inject<Ref<UserDetails>>("user_details");
const organizationDetails = inject<Ref<OrganizationDetails | null>>("organization_details");
const mobileAppDownloadUrl = computed(() =>
    organizationDetails?.value?.is_mobile_app_blocked || organizationDetails?.value?.is_mobile_app_promotion_hidden ? "" : props.endpoints.mobile_app_download,
);
const uiLanguage = computed(() => (props.user_profile.ui_language as string | null | undefined) ?? null);
</script>
